import React, { useState, useEffect, useCallback } from 'react';
import './Admin.css';
import AdminTodos from './admin/AdminTodos';
import AdminFinancials from './admin/AdminFinancials';
import AdminStaff from './admin/AdminStaff';
import AdminRegistrations from './admin/AdminRegistrations';
import { buildRegistrationsFromResponse } from './admin/registrations/registrationUtils';
import NurseHome from './admin/nurse/NurseHome';
import NurseIntake from './admin/nurse/NurseIntake';
import NurseMeds from './admin/nurse/NurseMeds';
import NurseIncidents from './admin/nurse/NurseIncidents';
import ChairmanHome from './admin/chairman/ChairmanHome';
import ChairmanRegistrations from './admin/chairman/ChairmanRegistrations';
import ChairmanFinancials from './admin/chairman/ChairmanFinancials';
import ChairmanReports from './admin/chairman/ChairmanReports';
import AdminAssignments from './admin/AdminAssignments';
import AdminCapacity from './admin/AdminCapacity';
import AdminSurvey from './admin/AdminSurvey';
import AdminReports from './admin/AdminReports';
import AdminVbs from './admin/AdminVbs';
import AdminSponsorships from './admin/AdminSponsorships';


export default function Admin() {
  const [token, setToken] = useState(localStorage.getItem('adminToken') || '');
  const [password, setPassword] = useState('');

  const [adminRole, setAdminRole] = useState(localStorage.getItem('adminRole') || '');
  const [adminName, setAdminName] = useState(localStorage.getItem('adminName') || '');

  const isSupplies = adminRole === 'supplies';
  const isNurse = adminRole === 'nurse';
  const isChairman = adminRole === 'chairman';


  // Nurse Intake search prefill (set from NurseHome quick links)
  const [nurseIntakePrefill, setNurseIntakePrefill] = useState('');

  // registrations (admin only full list)
  const [data, setData] = useState(null);

  // registrations count (admin + supplies)
  const [registrationCount, setRegistrationCount] = useState(null);
  const [registrationCountError, setRegistrationCountError] = useState('');
  const [registrationCountLoading, setRegistrationCountLoading] = useState(false);

  // supplies notes (admin + supplies)
  const [suppliesNotesData, setSuppliesNotesData] = useState({ headers: [], rows: [] });
  const [suppliesNotesError, setSuppliesNotesError] = useState('');
  const [suppliesNotesLoading, setSuppliesNotesLoading] = useState(false);

  // todos (admin)
  const [todosData, setTodosData] = useState({ headers: [], rows: [] });
  const [todoText, setTodoText] = useState('');
  const [todoNotes, setTodoNotes] = useState('');
  const [todoError, setTodoError] = useState('');
  const [todoLoading, setTodoLoading] = useState(false);
  const [todoNotesModal, setTodoNotesModal] = useState({ open: false, id: '', text: '', notes: '' });

  // ------------------------
  // Financials (admin + supplies)
  // ------------------------
  const [financeData, setFinanceData] = useState({ headers: [], rows: [] });
  const [financeLoading, setFinanceLoading] = useState(false);
  const [financeError, setFinanceError] = useState('');

  const [finDate, setFinDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [finCategory, setFinCategory] = useState('');
  const [finDescription, setFinDescription] = useState('');
  const [finAmount, setFinAmount] = useState('');

  // legacy fields (no longer used in UI; kept to send blank values)
  const [finPaidBy, setFinPaidBy] = useState('');
  const [finMethod, setFinMethod] = useState('');
  const [finNotes, setFinNotes] = useState('');

  // ------------------------
  // Staff (admin)
  // ------------------------
  const [staffData, setStaffData] = useState({ headers: [], rows: [] });
  const [staffLoading, setStaffLoading] = useState(false);
  const [staffError, setStaffError] = useState('');

  const [staffEmail, setStaffEmail] = useState('');
  const [staffName, setStaffName] = useState('');
  const [staffPhone, setStaffPhone] = useState('');

  // legacy/removed fields (kept to send blanks)
  const [staffRole, setStaffRole] = useState('');
  const [staffBgStatus, setStaffBgStatus] = useState('Pending');
  const [staffBgDate, setStaffBgDate] = useState('');
  const [staffMrStatus, setStaffMrStatus] = useState('Pending');
  const [staffMrDate, setStaffMrDate] = useState('');
  const [staffNotes, setStaffNotes] = useState('');

  // staff pricing
  const [staffRateType, setStaffRateType] = useState('free');
  const [staffRateAmount, setStaffRateAmount] = useState('');

  // UI (shared error/loading for current view)
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [vbsLoading, setVbsLoading] = useState(false);
  const [vbsError, setVbsError] = useState('');
  const [vbsRows, setVbsRows] = useState([]);

  // which view/tab is active after login
  const [activeTab, setActiveTab] = useState('home');

  // mobile menu (hamburger)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [registrationsMenuOpen, setRegistrationsMenuOpen] = useState(false);

  // ------------------------
  // Registrations: Fetch + normalize (admin only)
  // ------------------------
  const fetchRegistrations = useCallback(async (t) => {
    const authToken = t || localStorage.getItem('adminToken') || '';
    setError('');
    setLoading(true);
    try {
      const res = await fetch('/.netlify/functions/admin-get-registrations', {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      if (!res.ok) {
        const text = await res.text();
        setError(`Failed to load: ${res.status} ${text}`);
        setData(null);
        return;
      }

      const json = await res.json();
      const normalized = buildRegistrationsFromResponse(json);
      setData({ ...json, registrations: normalized });
    } catch (err) {
      setError('Network error while fetching registrations');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchVbsRegistrations = useCallback(async (t) => {
    const authToken = t || localStorage.getItem('adminToken') || '';
    setVbsError('');
    setVbsLoading(true);
    try {
      const res = await fetch('/.netlify/functions/admin-get-vbs-registrations', {
        headers: { Authorization: 'Bearer ' + authToken }
      });

      const text = await res.text();
      let json = {};
      try { json = text ? JSON.parse(text) : {}; } catch { json = { error: text }; }

      if (!res.ok) {
        setVbsError(json?.error || `Failed to load VBS registrations: ${res.status}`);
        setVbsRows([]);
        return;
      }

      setVbsRows(Array.isArray(json?.rows) ? json.rows : []);
    } catch (err) {
      setVbsError('Network error while fetching VBS registrations');
      setVbsRows([]);
    } finally {
      setVbsLoading(false);
    }
  }, []);

  // ------------------------
  // Registrations count (admin + supplies)
  // ------------------------
  const fetchRegistrationCount = useCallback(async (t) => {
    const authToken = t || localStorage.getItem('adminToken') || '';
    setRegistrationCountError('');
    setRegistrationCountLoading(true);
    try {
      const res = await fetch('/.netlify/functions/admin-get-registration-count', {
        headers: { Authorization: `Bearer ${authToken}` }
      });

      const text = await res.text();
      let json = {};
      try { json = text ? JSON.parse(text) : {}; } catch { json = { error: text }; }

      if (!res.ok) {
        setRegistrationCount(null);
        setRegistrationCountError(json?.error || `Failed to load count: ${res.status}`);
        return;
      }

      setRegistrationCount(Number(json.count));
    } catch (err) {
      setRegistrationCount(null);
      setRegistrationCountError('Network error while fetching registration count');
    } finally {
      setRegistrationCountLoading(false);
    }
  }, []);

  // ------------------------
  // Supplies notes (admin + supplies)
  // ------------------------
  const fetchSuppliesNotes = useCallback(async (t) => {
    const authToken = t || localStorage.getItem('adminToken') || '';
    setSuppliesNotesError('');
    setSuppliesNotesLoading(true);
    try {
      const res = await fetch('/.netlify/functions/admin-get-supplies-notes', {
        headers: { Authorization: `Bearer ${authToken}` }
      });

      const text = await res.text();
      let json = {};
      try { json = text ? JSON.parse(text) : {}; } catch { json = { error: text }; }

      if (!res.ok) {
        setSuppliesNotesError(json?.error || `Failed to load supplies notes: ${res.status}`);
        setSuppliesNotesData({ headers: [], rows: [] });
        return;
      }

      const rows = Array.isArray(json.rows) ? json.rows : [];
      rows.sort((a, b) => {
        const at = Date.parse(a.createdAt || a.created_at || '') || 0;
        const bt = Date.parse(b.createdAt || b.created_at || '') || 0;
        return bt - at;
      });

      setSuppliesNotesData({ ...json, rows });
    } catch (err) {
      setSuppliesNotesError('Network error while fetching supplies notes');
      setSuppliesNotesData({ headers: [], rows: [] });
    } finally {
      setSuppliesNotesLoading(false);
    }
  }, []);

  const addSuppliesNote = useCallback(async (noteText) => {
    const authToken = token || localStorage.getItem('adminToken') || '';
    const note = String(noteText || '').trim();
    if (!note) {
      setSuppliesNotesError('Please enter a note.');
      return;
    }

    setSuppliesNotesError('');
    setSuppliesNotesLoading(true);
    try {
      const res = await fetch('/.netlify/functions/admin-add-supplies-note', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ note })
      });

      const raw = await res.text();
      let json = {};
      try { json = raw ? JSON.parse(raw) : {}; } catch { json = { error: raw }; }

      if (!res.ok) {
        setSuppliesNotesError(json?.error || `Failed to add note: ${res.status}`);
        return;
      }

      await fetchSuppliesNotes(authToken);
    } catch (err) {
      setSuppliesNotesError('Network error while adding note');
    } finally {
      setSuppliesNotesLoading(false);
    }
  }, [token, fetchSuppliesNotes]);

  // ------------------------
  // Todos (admin only)
  // ------------------------
  const fetchTodos = useCallback(async (t) => {
    const authToken = t || localStorage.getItem('adminToken') || '';
    setTodoError('');
    setTodoLoading(true);
    try {
      const res = await fetch('/.netlify/functions/admin-get-todos', {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      const text = await res.text();
      let json = {};
      try { json = text ? JSON.parse(text) : {}; } catch { json = { error: text }; }

      if (!res.ok) {
        setTodoError(json && json.error ? json.error : `Failed to load todos: ${res.status}`);
        setTodosData({ headers: [], rows: [] });
        return;
      }

      const rows = Array.isArray(json.rows) ? json.rows : [];
      rows.sort((a, b) => {
        const ad = String(a.done || '').toLowerCase() === 'true';
        const bd = String(b.done || '').toLowerCase() === 'true';
        if (ad !== bd) return ad ? 1 : -1;

        const at = Date.parse(a.createdAt || a.created_at || '') || 0;
        const bt = Date.parse(b.createdAt || b.created_at || '') || 0;
        return bt - at;
      });

      setTodosData({ ...json, rows });
    } catch (err) {
      setTodoError('Network error while fetching todos');
      setTodosData({ headers: [], rows: [] });
    } finally {
      setTodoLoading(false);
    }
  }, []);

  const addTodo = async () => {
    const authToken = token || localStorage.getItem('adminToken') || '';
    setTodoError('');
    const text = (todoText || '').trim();
    const notes = (todoNotes || '').trim();

    if (!text && !notes) {
      setTodoError('Please enter a task or a note.');
      return;
    }

    try {
      const res = await fetch('/.netlify/functions/admin-add-todo', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ text, notes })
      });
      const raw = await res.text();
      let json = {};
      try { json = raw ? JSON.parse(raw) : {}; } catch { json = { error: raw }; }

      if (!res.ok) {
        setTodoError(json && json.error ? json.error : `Failed to add item: ${res.status}`);
        return;
      }

      setTodoText('');
      setTodoNotes('');
      await fetchTodos(authToken);
    } catch (err) {
      setTodoError('Network error while adding item');
    }
  };

  const toggleTodoDone = async (id, nextDone) => {
    const authToken = token || localStorage.getItem('adminToken') || '';
    setTodoError('');
    try {
      const res = await fetch('/.netlify/functions/admin-toggle-todo', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ id, done: nextDone ? 'true' : 'false' })
      });

      const raw = await res.text();
      let json = {};
      try { json = raw ? JSON.parse(raw) : {}; } catch { json = { error: raw }; }

      if (!res.ok) {
        setTodoError(json && json.error ? json.error : `Failed to update todo: ${res.status}`);
        return;
      }

      await fetchTodos(authToken);
    } catch (err) {
      setTodoError('Network error while updating todo');
    }
  };

  const saveTodoNotes = async () => {
    const authToken = token || localStorage.getItem('adminToken') || '';
    const id = String(todoNotesModal.id || '').trim();
    if (!id) return;

    setTodoError('');
    setTodoLoading(true);
    try {
      const res = await fetch('/.netlify/functions/admin-update-todo-notes', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ id, notes: todoNotesModal.notes })
      });

      const raw = await res.text();
      let json = {};
      try { json = raw ? JSON.parse(raw) : {}; } catch { json = { error: raw }; }

      if (!res.ok) {
        setTodoError(json && json.error ? json.error : `Failed to update notes: ${res.status}`);
        return;
      }

      setTodoNotesModal({ open: false, id: '', text: '', notes: '' });
      await fetchTodos(authToken);
    } catch (err) {
      setTodoError('Network error while updating notes');
    } finally {
      setTodoLoading(false);
    }
  };

  const deleteTodo = async (id) => {
    const authToken = token || localStorage.getItem('adminToken') || '';
    const cleanId = String(id || '').trim();
    if (!cleanId) return;

    setTodoError('');
    setTodoLoading(true);
    try {
      const res = await fetch('/.netlify/functions/admin-delete-todo', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ id: cleanId })
      });

      const raw = await res.text();
      let json = {};
      try { json = raw ? JSON.parse(raw) : {}; } catch { json = { error: raw }; }

      if (!res.ok) {
        setTodoError(json && json.error ? json.error : `Failed to delete item: ${res.status}`);
        return;
      }

      if (todoNotesModal?.open && String(todoNotesModal.id || '').trim() === cleanId) {
        setTodoNotesModal({ open: false, id: '', text: '', notes: '' });
      }

      await fetchTodos(authToken);
    } catch (err) {
      setTodoError('Network error while deleting item');
    } finally {
      setTodoLoading(false);
    }
  };

  // ------------------------
  // Financials: Fetch/Add/Import (admin + supplies)
  // ------------------------
  const fetchFinance = useCallback(async (t) => {
    const authToken = t || localStorage.getItem('adminToken') || '';
    setFinanceError('');
    setFinanceLoading(true);
    try {
      const res = await fetch('/.netlify/functions/admin-get-finance', {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      const text = await res.text();
      let json = {};
      try { json = text ? JSON.parse(text) : {}; } catch { json = { error: text }; }

      if (!res.ok) {
        setFinanceError(json && json.error ? json.error : `Failed to load finance: ${res.status}`);
        setFinanceData({ headers: [], rows: [] });
        return;
      }

      const rows = Array.isArray(json.rows) ? json.rows : [];
      rows.sort((a, b) => {
        const at = Date.parse(a.createdAt || a.created_at || '') || 0;
        const bt = Date.parse(b.createdAt || b.created_at || '') || 0;
        return bt - at;
      });

      setFinanceData({ ...json, rows });
    } catch (err) {
      setFinanceError('Network error while fetching finance');
      setFinanceData({ headers: [], rows: [] });
    } finally {
      setFinanceLoading(false);
    }
  }, []);

  const addFinance = async (type) => {
    const authToken = token || localStorage.getItem('adminToken') || '';
    setFinanceError('');
    setFinanceLoading(true);
    try {
      const payload = {
        date: finDate,
        type,
        category: finCategory,
        description: finDescription,
        amount: finAmount,
        paidBy: finPaidBy,
        method: finMethod,
        notes: finNotes
      };

      const res = await fetch('/.netlify/functions/admin-add-finance', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      const raw = await res.text();
      let json = {};
      try { json = raw ? JSON.parse(raw) : {}; } catch { json = { error: raw }; }

      if (!res.ok) {
        setFinanceError(json && json.error ? json.error : `Failed to add entry: ${res.status}`);
        return;
      }

      setFinDescription('');
      setFinAmount('');
      setFinPaidBy('');
      setFinMethod('');
      setFinNotes('');

      await fetchFinance(authToken);
    } catch (err) {
      setFinanceError('Network error while adding entry');
    } finally {
      setFinanceLoading(false);
    }
  };

  // ------------------------
  // Staff: Fetch/Upsert (admin only)
  // ------------------------
  const fetchStaff = useCallback(async (t) => {
    const authToken = t || localStorage.getItem('adminToken') || '';
    setStaffError('');
    setStaffLoading(true);
    try {
      const res = await fetch('/.netlify/functions/admin-get-staff', {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      const text = await res.text();
      let json = {};
      try { json = text ? JSON.parse(text) : {}; } catch { json = { error: text }; }

      if (!res.ok) {
        setStaffError(json && json.error ? json.error : `Failed to load staff: ${res.status}`);
        setStaffData({ headers: [], rows: [] });
        return;
      }

      const rows = Array.isArray(json.rows) ? json.rows : [];
      rows.sort((a, b) => String(a.email || '').localeCompare(String(b.email || '')));
      setStaffData({ ...json, rows });
    } catch (err) {
      setStaffError('Network error while fetching staff');
      setStaffData({ headers: [], rows: [] });
    } finally {
      setStaffLoading(false);
    }
  }, []);

  // ------------------------
  // Tabs (for menu)
  // ------------------------
  const tabs = (
    isNurse
      ? [
        { id: 'home', label: 'Home', show: true },
        { id: 'nurse_intake', label: 'From registrations', show: true },
        { id: 'nurse_meds', label: 'Meds', show: true },
        { id: 'nurse_incidents', label: 'Incidents', show: true }
      ]
      : isChairman
        ? [
          { id: 'home', label: 'Home', show: true },
          { id: 'chairman_registrations', label: 'Registrations', show: true },
          { id: 'chairman_financials', label: 'Financials', show: true },
          { id: 'chairman_reports', label: 'Reports', show: true }
        ]
        : [
          { id: 'home', label: 'Home', show: true },
          { id: 'todos', label: 'Tasks', show: !isSupplies },
          { id: 'financials', label: 'Financials', show: true },
          { id: 'staff', label: 'Staff', show: !isSupplies },
          { id: 'registrations', label: 'Registrations', show: !isSupplies },
          { id: 'vbs', label: 'VBS', show: !isSupplies },
          { id: 'survey', label: 'Survey', show: !isSupplies },
          { id: 'assignments', label: 'Room Assignments', show: !isSupplies },
          { id: 'capacity', label: 'Capacity / Housing', show: !isSupplies },
          { id: 'sponsorships', label: 'Sponsorships', show: !isSupplies },
          { id: 'reports', label: 'Reports', show: !isSupplies }
        ]
  ).filter((t) => t.show);

  // ------------------------
  // Header refresh icon: refresh EVERYTHING
  // ------------------------
  const refreshCurrentTab = useCallback(() => {
    if (!token) return;

    // Nurse should not call admin/supplies refresh endpoints
    if (isNurse) return;

    if (isChairman) {
      // Chairman pages fetch inside their own components,
      // so we don't call admin-only endpoints here.
      return;
    }

    if (activeTab === 'home') {
      fetchRegistrationCount(token);
      fetchSuppliesNotes(token);
      fetchFinance(token);
      if (!isSupplies) fetchTodos(token);
      return;
    }
    if (activeTab === 'todos') {
      if (!isSupplies) fetchTodos(token);
      return;
    }
    if (activeTab === 'financials') {
      fetchFinance(token);
      return;
    }
    if (activeTab === 'staff') {
      fetchStaff(token);
      return;
    }
    if (activeTab === 'registrations') {
      fetchRegistrations(token);
      return;
    }
    if (activeTab === 'vbs') {
      fetchVbsRegistrations(token);
    }
  }, [
    token,
    isNurse,
    isChairman,
    isSupplies,
    activeTab,
    fetchRegistrationCount,
    fetchSuppliesNotes,
    fetchFinance,
    fetchTodos,
    fetchStaff,
    fetchRegistrations,
    fetchVbsRegistrations
  ]);

  // ------------------------
  // Load data after login
  // ------------------------
  useEffect(() => {
    if (!token) return;
    if (isSupplies) return; // supplies should not auto-load admin todos
    if (activeTab === 'todos') fetchTodos(token);
  }, [token, isSupplies, activeTab, fetchTodos]);

  useEffect(() => {
    if (!token) return;
    if (activeTab === 'financials' || activeTab === 'reports') fetchFinance(token);
  }, [token, activeTab, fetchFinance]);

  useEffect(() => {
    if (!token) return;
    if (isSupplies) return; // supplies should not access staff
    if (activeTab === 'staff') fetchStaff(token);
  }, [token, activeTab, isSupplies, fetchStaff]);

  useEffect(() => {
    if (!token) return;
    if (isNurse) return;
    if (isChairman) return;

    if (activeTab === 'home') {
      fetchRegistrationCount(token);
      fetchSuppliesNotes(token);
      fetchFinance(token);
    }
  }, [token, activeTab, isNurse, isChairman, isSupplies, fetchRegistrationCount, fetchSuppliesNotes, fetchFinance, fetchTodos]);

  useEffect(() => {
    if (!token) return;
    if (isSupplies) return;
    if (isChairman) return;

    if ((activeTab === 'registrations' || activeTab === 'assignments' || activeTab === 'reports') && !data) fetchRegistrations(token);
    if (activeTab === 'vbs' && !vbsRows.length) fetchVbsRegistrations(token);
  }, [token, activeTab, isSupplies, isChairman, data, fetchRegistrations, vbsRows.length, fetchVbsRegistrations]);

  // close hamburger menu when switching tabs
  useEffect(() => {
    setMobileMenuOpen(false);
    setRegistrationsMenuOpen(false);
  }, [activeTab]);

  async function handleLogin(e) {
    e.preventDefault();
    setError('');
    try {
      const res = await fetch('/.netlify/functions/admin-login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password })
      });

      const raw = await res.text();
      let json = {};
      try { json = raw ? JSON.parse(raw) : {}; } catch { json = { error: raw }; }

      if (!res.ok) {
        setError('Login failed');
        return;
      }

      const t = json.token || '';
      const role = json.role || '';
      const name = json.name || '';

      if (!t) {
        setError('Login failed (missing token).');
        return;
      }

      localStorage.setItem('adminToken', t);
      localStorage.setItem('adminRole', role);
      localStorage.setItem('adminName', name);

      setToken(t);
      setAdminRole(role);
      setAdminName(name);

      // default tab:
      setActiveTab(role === 'nurse' ? 'home' : 'home');
    } catch (err) {
      setError('Network error during login');
    }
  }

  function handleLogout() {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminRole');
    localStorage.removeItem('adminName');

    setToken('');
    setAdminRole('');
    setAdminName('');

    setPassword('');
    setActiveTab('home');
    setMobileMenuOpen(false);

    setData(null);

    setTodosData({ headers: [], rows: [] });
    setTodoText('');
    setTodoNotes('');
    setTodoError('');
    setTodoLoading(false);
    setTodoNotesModal({ open: false, id: '', text: '', notes: '' });

    setFinanceData({ headers: [], rows: [] });
    setFinanceError('');

    setStaffData({ headers: [], rows: [] });
    setStaffError('');

    setRegistrationCount(null);
    setRegistrationCountError('');
    setRegistrationCountLoading(false);

    setSuppliesNotesData({ headers: [], rows: [] });
    setSuppliesNotesError('');
    setSuppliesNotesLoading(false);
  }

  const registrations = data && Array.isArray(data.registrations) ? data.registrations : [];

  const suppliesNotesRows = Array.isArray(suppliesNotesData?.rows) ? suppliesNotesData.rows : [];

  const toNumber = (v) => {
    const n = Number(String(v ?? '').replace(/[^0-9.-]/g, ''));
    return Number.isFinite(n) ? n : 0;
  };

  const fmtUsd = (n) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(toNumber(n));

  const financeRows = Array.isArray(financeData?.rows) ? financeData.rows : [];
  const totalIncome = financeRows
    .filter((r) => String(r?.type || '').toLowerCase() === 'income')
    .reduce((sum, r) => sum + toNumber(r.amount), 0);

  const totalExpense = financeRows
    .filter((r) => String(r?.type || '').toLowerCase() === 'expense')
    .reduce((sum, r) => sum + toNumber(r.amount), 0);

  const net = totalIncome - totalExpense;

  const upsertStaff = async () => {
    const authToken = token || localStorage.getItem('adminToken') || '';
    setStaffError('');

    const email = (staffEmail || '').trim().toLowerCase();
    if (!email) {
      setStaffError('Email is required.');
      return;
    }

    setStaffLoading(true);
    try {
      const payload = {
        email,
        name: staffName,
        phone: staffPhone,
        role: staffRole,
        backgroundCheckStatus: staffBgStatus,
        backgroundCheckDate: '',
        mandatedReporterStatus: staffMrStatus,
        mandatedReporterDate: '',
        notes: '',
        registrationRateType: staffRateType,
        registrationRateAmount: staffRateAmount
      };

      const res = await fetch('/.netlify/functions/admin-upsert-staff', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      const raw = await res.text();
      let json = {};
      try { json = raw ? JSON.parse(raw) : {}; } catch { json = { error: raw }; }

      if (!res.ok) {
        setStaffError(json && json.error ? json.error : `Failed to save staff: ${res.status}`);
        return;
      }

      setStaffEmail('');
      setStaffName('');
      setStaffPhone('');

      setStaffRole('');
      setStaffBgStatus('Pending');
      setStaffBgDate('');
      setStaffMrStatus('Pending');
      setStaffMrDate('');
      setStaffNotes('');
      setStaffRateType('free');
      setStaffRateAmount('');

      await fetchStaff(authToken);
    } catch (err) {
      setStaffError('Network error while saving staff');
    } finally {
      setStaffLoading(false);
    }
  };

  const loadStaffIntoForm = (row) => {
    setStaffEmail(String(row.email || '').trim());
    setStaffName(String(row.name || '').trim());
    setStaffPhone(String(row.phone || '').trim());

    setStaffRole(String(row.role || '').trim());
    setStaffNotes('');
    setStaffBgStatus(String(row.backgroundCheckStatus || 'Pending').trim() || 'Pending');
    setStaffBgDate('');
    setStaffMrStatus(String(row.mandatedReporterStatus || 'Pending').trim() || 'Pending');
    setStaffMrDate('');

    setStaffRateType(String(row.registrationRateType || 'free').trim() || 'free');
    setStaffRateAmount(String(row.registrationRateAmount || '').trim());
  };

  return (
    <div className="admin-root">
      <div className="admin-page">
        <div className="card admin-card">
          <div className="admin-header">
            <h1 className="admin-title">Admin Console</h1>

            {token ? (
              <div className="admin-header-actions">
                <button
                  type="button"
                  className="btn icon-btn"
                  title="Refresh"
                  aria-label="Refresh"
                  onClick={refreshCurrentTab}
                >
                  <span aria-hidden="true">⟳</span>
                </button>

                <button type="button" className="btn" onClick={handleLogout}>
                  Logout
                </button>

                <button
                  type="button"
                  className="btn icon-btn hamburger-btn"
                  aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
                  aria-expanded={mobileMenuOpen ? 'true' : 'false'}
                  onClick={() => setMobileMenuOpen((v) => !v)}
                >
                  <span aria-hidden="true">☰</span>
                </button>
              </div>
            ) : null}
          </div>

          {!token && (
            <>
              <p>Login to access the admin console.</p>
              <form onSubmit={handleLogin} className="admin-login">
                <input
                  type="password"
                  placeholder="Admin password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                <button type="submit">Login</button>
                {error && <div className="error">{error}</div>}
              </form>
            </>
          )}

          {token && (
            <>
              {/* Universal hamburger menu (desktop + mobile) */}
              <div className={`admin-mobile-menu ${mobileMenuOpen ? 'open' : ''}`}>
                {/* Top-level tabs (exclude ones we want under Registrations) */}
                {tabs
                  .filter((t) => !['registrations', 'assignments', 'capacity', 'sponsorships', 'vbs'].includes(t.id))
                  .map((t) => (
                    <button
                      key={t.id}
                      className={`btn ${activeTab === t.id ? 'primary' : ''}`}
                      onClick={() => {
                        setActiveTab(t.id);
                        setMobileMenuOpen(false);
                      }}
                    >
                      {t.label}
                    </button>
                  ))}

                {/* Registrations group (collapsible) */}
                {tabs.some((t) => t.id === 'registrations') ? (
                  <>
                    <button
                      type="button"
                      className={`btn ${['registrations', 'assignments', 'capacity', 'sponsorships', 'vbs'].includes(activeTab) ? 'primary' : ''
                        }`}
                      onClick={() => setRegistrationsMenuOpen((v) => !v)}
                    >
                      Registrations{' '}
                      {(registrationsMenuOpen || ['registrations', 'assignments', 'capacity', 'vbs'].includes(activeTab))
                        ? '▾'
                        : '▸'}
                    </button>

                    {(registrationsMenuOpen || ['registrations', 'assignments', 'capacity', 'sponsorships', 'vbs'].includes(activeTab)) ? (
                      <div style={{ display: 'grid', gap: 6, marginTop: 6, paddingLeft: 14 }}>
                        <button
                          type="button"
                          className={`btn ${activeTab === 'registrations' ? 'primary' : ''}`}
                          onClick={() => {
                            setActiveTab('registrations');
                            setMobileMenuOpen(false);
                          }}
                        >
                          All Registrations
                        </button>

                        {tabs.some((t) => t.id === 'vbs') ? (
                          <button
                            type="button"
                            className={`btn ${activeTab === 'vbs' ? 'primary' : ''}`}
                            onClick={() => {
                              setActiveTab('vbs');
                              setMobileMenuOpen(false);
                            }}
                          >
                            VBS
                          </button>
                        ) : null}

                        {tabs.some((t) => t.id === 'assignments') ? (
                          <button
                            type="button"
                            className={`btn ${activeTab === 'assignments' ? 'primary' : ''}`}
                            onClick={() => {
                              setActiveTab('assignments');
                              setMobileMenuOpen(false);
                            }}
                          >
                            Room Assignments
                          </button>
                        ) : null}

                        <button
                          className={`btn ${activeTab === 'sponsorships' ? 'primary' : ''}`}
                          onClick={() => {
                            setActiveTab('sponsorships');
                            setRegistrationsMenuOpen(false);
                          }}
                          style={{ width: '100%', textAlign: 'left', marginTop: 8 }}
                        >
                          Sponsorships
                        </button>
                        
                        {tabs.some((t) => t.id === 'capacity') ? (
                          <button
                            type="button"
                            className={`btn ${activeTab === 'capacity' ? 'primary' : ''}`}
                            onClick={() => {
                              setActiveTab('capacity');
                              setMobileMenuOpen(false);
                            }}
                          >
                            Capacity
                          </button>
                        ) : null}
                      </div>
                    ) : null}
                  </>
                ) : null}

                {adminName ? (
                  <div className="admin-mobile-whoami">
                    Logged in as: <strong>{adminName}</strong>
                    {adminRole ? ' (' + adminRole + ')' : ''}
                  </div>
                ) : null}
              </div>

              {activeTab === 'home' && (
                isNurse ? (
                  <NurseHome
                    token={token}
                    adminName={adminName}
                    adminRole={adminRole}
                    onGoToIntake={(prefill) => {
                      setNurseIntakePrefill(prefill || '');
                      setActiveTab('nurse_intake');
                    }}
                    onGoToMeds={() => setActiveTab('nurse_meds')}
                    onGoToIncidents={() => setActiveTab('nurse_incidents')}
                  />
                ) : isChairman ? (
                  <ChairmanHome token={token} />
                ) : (
                  <div style={{ marginTop: 12, display: 'grid', gridTemplateColumns: '1fr', gap: 12 }}>
                    {/* Stat cards */}
                    <div className="card">
                      <div
                        style={{
                          marginTop: 0,
                          display: 'grid',
                          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                          gap: 10
                        }}
                      >
                        <div style={{ border: '1px solid #eee', borderRadius: 12, padding: 12, background: '#fff' }}>
                          <div className="muted" style={{ fontSize: 12 }}>Registrations</div>
                          {registrationCountLoading ? (
                            <div style={{ marginTop: 6 }}>Loading…</div>
                          ) : registrationCountError ? (
                            <div className="error" style={{ marginTop: 6 }}>{registrationCountError}</div>
                          ) : typeof registrationCount === 'number' ? (
                            <div style={{ fontSize: 36, fontWeight: 900, marginTop: 6 }}>{registrationCount}</div>
                          ) : (
                            <div className="muted" style={{ marginTop: 6 }}>—</div>
                          )}
                        </div>

                        <div style={{ border: '1px solid #eee', borderRadius: 12, padding: 12, background: '#fff' }}>
                          <div className="muted" style={{ fontSize: 12 }}>Net</div>
                          {financeLoading ? (
                            <div style={{ marginTop: 6 }}>Loading…</div>
                          ) : financeError ? (
                            <div className="error" style={{ marginTop: 6 }}>{financeError}</div>
                          ) : (
                            <div style={{ fontSize: 28, fontWeight: 900, marginTop: 6, color: net < 0 ? '#b42318' : '#067647' }}>
                              {fmtUsd(net)}
                            </div>
                          )}
                        </div>

                        <div style={{ border: '1px solid #eee', borderRadius: 12, padding: 12, background: '#fff' }}>
                          <div className="muted" style={{ fontSize: 12 }}>Income</div>
                          {financeLoading ? (
                            <div style={{ marginTop: 6 }}>Loading…</div>
                          ) : (
                            <div style={{ fontSize: 22, fontWeight: 800, marginTop: 6 }}>{fmtUsd(totalIncome)}</div>
                          )}
                        </div>

                        <div style={{ border: '1px solid #eee', borderRadius: 12, padding: 12, background: '#fff' }}>
                          <div className="muted" style={{ fontSize: 12 }}>Expenses</div>
                          {financeLoading ? (
                            <div style={{ marginTop: 6 }}>Loading…</div>
                          ) : (
                            <div style={{ fontSize: 22, fontWeight: 800, marginTop: 6 }}>{fmtUsd(totalExpense)}</div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Todos (tasks + notes) — admin only, not supplies */}
                    {!isSupplies && (
                      <AdminTodos
                        token={token}
                        todosData={todosData}
                        todoText={todoText}
                        setTodoText={setTodoText}
                        todoNotes={todoNotes}
                        setTodoNotes={setTodoNotes}
                        todoError={todoError}
                        todoLoading={todoLoading}
                        addTodo={addTodo}
                        fetchTodos={fetchTodos}
                        toggleTodoDone={toggleTodoDone}
                        todoNotesModal={todoNotesModal}
                        setTodoNotesModal={setTodoNotesModal}
                        saveTodoNotes={saveTodoNotes}
                        deleteTodo={deleteTodo}
                      />
                    )}

                    {/* Supplies Notes — read-only for admin, writable for supplies role */}
                    <div className="card">
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', alignItems: 'baseline' }}>
                        <div>
                          <h2 style={{ margin: 0 }}>Supplies Notes</h2>
                          <div className="muted" style={{ marginTop: 4 }}>
                            {isSupplies
                              ? 'Add reminders, packing lists, meal counts, and updates.'
                              : 'Kitchen updates (read-only here).'}
                          </div>
                        </div>
                      </div>

                      {isSupplies ? (
                        /* Full write UI for supplies role */
                        <div
                          style={{
                            marginTop: 12,
                            display: 'grid',
                            gridTemplateColumns: 'minmax(260px, 360px) 1fr',
                            gap: 12,
                            alignItems: 'start'
                          }}
                        >
                          {/* Left: Add note + meal count */}
                          <div style={{ border: '1px solid #eee', borderRadius: 12, padding: 12, background: '#fff' }}>
                            <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>Add a note</div>
                            <textarea
                              id="supplies-note-input"
                              placeholder="Example: Need 3 cases of water. Pick up paper towels."
                              rows={4}
                              style={{
                                width: '100%',
                                padding: '10px 12px',
                                borderRadius: 10,
                                border: '1px solid #ddd',
                                boxSizing: 'border-box',
                                resize: 'vertical'
                              }}
                            />

                            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
                              <button
                                className="btn primary"
                                type="button"
                                disabled={suppliesNotesLoading}
                                onClick={() => {
                                  const el = document.getElementById('supplies-note-input');
                                  addSuppliesNote(el?.value || '');
                                  if (el) el.value = '';
                                }}
                              >
                                Add note
                              </button>

                              <button
                                className="btn"
                                type="button"
                                disabled={suppliesNotesLoading}
                                onClick={() => {
                                  const el = document.getElementById('supplies-note-input');
                                  if (el) el.value = '';
                                }}
                              >
                                Clear
                              </button>
                            </div>

                            <div style={{ marginTop: 14, borderTop: '1px solid #eee', paddingTop: 12 }}>
                              <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>Meal count</div>

                              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                                <select
                                  style={{
                                    width: '100%',
                                    padding: '10px 12px',
                                    borderRadius: 10,
                                    border: '1px solid #ddd',
                                    boxSizing: 'border-box',
                                    background: '#fff'
                                  }}
                                >
                                  <option value="Breakfast">Breakfast</option>
                                  <option value="Lunch">Lunch</option>
                                  <option value="Dinner">Dinner</option>
                                </select>

                                <input
                                  type="number"
                                  placeholder="Count"
                                  style={{
                                    width: '100%',
                                    padding: '10px 12px',
                                    borderRadius: 10,
                                    border: '1px solid #ddd',
                                    boxSizing: 'border-box'
                                  }}
                                />
                              </div>

                              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
                                <button
                                  className="btn"
                                  disabled={suppliesNotesLoading}
                                >
                                  Add meal count
                                </button>
                              </div>
                            </div>

                            {suppliesNotesError ? <div className="error" style={{ marginTop: 10 }}>{suppliesNotesError}</div> : null}
                          </div>

                          {/* Right: Recent notes */}
                          <div style={{ minWidth: 0 }}>
                            {suppliesNotesLoading ? (
                              <div>Loading notes…</div>
                            ) : suppliesNotesRows.length ? (
                              <div style={{ display: 'grid', gap: 10 }}>
                                {suppliesNotesRows.slice(0, 20).map((r) => {
                                  const id = String(r.id || r.ID || '').trim() || Math.random().toString(16).slice(2);
                                  const note = String(r.note || r.Note || '').trim();
                                  const createdAt = r.createdAt ? new Date(r.createdAt).toLocaleString() : '';
                                  const createdBy = String(r.createdBy || r.created_by || '').trim();
                                  const date = String(r.date || '').trim();

                                  return (
                                    <div key={id} style={{ border: '1px solid #eee', borderRadius: 12, padding: '12px 12px', background: '#fff' }}>
                                      <div style={{ whiteSpace: 'pre-wrap' }}>{note}</div>
                                      <div className="muted" style={{ marginTop: 8, fontSize: 12 }}>
                                        {(date ? `${date} • ` : '')}{createdBy ? `by ${createdBy} • ` : ''}{createdAt}
                                      </div>
                                    </div>
                                  );
                                })}

                                {suppliesNotesRows.length > 20 ? (
                                  <div className="muted" style={{ fontSize: 12 }}>
                                    Showing newest 20 notes.
                                  </div>
                                ) : null}
                              </div>
                            ) : (
                              <div className="muted">No notes yet.</div>
                            )}
                          </div>
                        </div>
                      ) : (
                        /* Read-only list for admin role */
                        <div style={{ marginTop: 12 }}>
                          {suppliesNotesLoading ? (
                            <div>Loading notes…</div>
                          ) : suppliesNotesRows.length ? (
                            <div style={{ display: 'grid', gap: 10 }}>
                              {suppliesNotesRows.slice(0, 20).map((r) => {
                                const id = String(r.id || r.ID || '').trim() || Math.random().toString(16).slice(2);
                                const note = String(r.note || r.Note || '').trim();
                                const createdAt = r.createdAt ? new Date(r.createdAt).toLocaleString() : '';
                                const createdBy = String(r.createdBy || r.created_by || '').trim();
                                const date = String(r.date || '').trim();

                                return (
                                  <div key={id} style={{ border: '1px solid #eee', borderRadius: 12, padding: '12px 12px', background: '#fff' }}>
                                    <div style={{ whiteSpace: 'pre-wrap' }}>{note}</div>
                                    <div className="muted" style={{ marginTop: 8, fontSize: 12 }}>
                                      {(date ? `${date} • ` : '')}{createdBy ? `by ${createdBy} • ` : ''}{createdAt}
                                    </div>
                                  </div>
                                );
                              })}

                              {suppliesNotesRows.length > 20 ? (
                                <div className="muted" style={{ fontSize: 12 }}>
                                  Showing newest 20 notes.
                                </div>
                              ) : null}
                            </div>
                          ) : (
                            <div className="muted">No notes yet.</div>
                          )}
                        </div>
                      )}

                      {suppliesNotesError ? (
                        <div className="error" style={{ marginTop: 10 }}>{suppliesNotesError}</div>
                      ) : null}
                    </div>
                  </div>
                )
              )}

              {isNurse && activeTab === 'nurse_intake' && (
                <NurseIntake token={token} prefillQuery={nurseIntakePrefill} />
              )}
              {isNurse && activeTab === 'nurse_meds' && <NurseMeds token={token} />}
              {isNurse && activeTab === 'nurse_incidents' && <NurseIncidents token={token} />}

              {!isSupplies && activeTab === 'todos' && (
                <AdminTodos
                  token={token}
                  todosData={todosData}
                  todoText={todoText}
                  setTodoText={setTodoText}
                  todoNotes={todoNotes}
                  setTodoNotes={setTodoNotes}
                  todoError={todoError}
                  todoLoading={todoLoading}
                  addTodo={addTodo}
                  fetchTodos={fetchTodos}
                  toggleTodoDone={toggleTodoDone}
                  todoNotesModal={todoNotesModal}
                  setTodoNotesModal={setTodoNotesModal}
                  saveTodoNotes={saveTodoNotes}
                  deleteTodo={deleteTodo}
                />
              )}

              {activeTab === 'financials' && (
                <AdminFinancials
                  token={token}
                  financeData={financeData}
                  financeLoading={financeLoading}
                  financeError={financeError}
                  finDate={finDate}
                  setFinDate={setFinDate}
                  finCategory={finCategory}
                  setFinCategory={setFinCategory}
                  finDescription={finDescription}
                  setFinDescription={setFinDescription}
                  finAmount={finAmount}
                  setFinAmount={setFinAmount}
                  finMethod={finMethod}
                  setFinMethod={setFinMethod}
                  finNotes={finNotes}
                  setFinNotes={setFinNotes}
                  fetchFinance={fetchFinance}
                  addFinance={addFinance}
                />
              )}


              {isChairman && activeTab === 'chairman_registrations' && (
                <ChairmanRegistrations token={token} />
              )}

              {isChairman && activeTab === 'chairman_financials' && (
                <ChairmanFinancials token={token} />
              )}

              {isChairman && activeTab === 'chairman_reports' && (
                <ChairmanReports token={token} />
              )}

              {!isSupplies && activeTab === 'staff' && (
                <AdminStaff
                  token={token}
                  staffData={staffData}
                  staffLoading={staffLoading}
                  staffError={staffError}
                  staffEmail={staffEmail}
                  setStaffEmail={setStaffEmail}
                  staffName={staffName}
                  setStaffName={setStaffName}
                  staffPhone={staffPhone}
                  setStaffPhone={setStaffPhone}
                  staffRole={staffRole}
                  setStaffRole={setStaffRole}
                  staffBgStatus={staffBgStatus}
                  setStaffBgStatus={setStaffBgStatus}
                  staffBgDate={staffBgDate}
                  setStaffBgDate={setStaffBgDate}
                  staffMrStatus={staffMrStatus}
                  setStaffMrStatus={setStaffMrStatus}
                  staffMrDate={staffMrDate}
                  setStaffMrDate={setStaffMrDate}
                  staffNotes={staffNotes}
                  setStaffNotes={setStaffNotes}
                  staffRateType={staffRateType}
                  setStaffRateType={setStaffRateType}
                  staffRateAmount={staffRateAmount}
                  setStaffRateAmount={setStaffRateAmount}
                  fetchStaff={fetchStaff}
                  upsertStaff={upsertStaff}
                  loadStaffIntoForm={loadStaffIntoForm}
                />
              )}

              {!isSupplies && activeTab === 'registrations' && (
                <AdminRegistrations
                  token={token}
                  loading={loading}
                  error={error}
                  fetchRegistrations={fetchRegistrations}
                  registrations={registrations}
                />
              )}
              {!isSupplies && activeTab === 'assignments' && (
                <AdminAssignments
                  token={token}
                  registrations={registrations} />
              )}
              {!isSupplies && activeTab === 'vbs' && (
                <AdminVbs
                  loading={vbsLoading}
                  error={vbsError}
                  rows={vbsRows}
                  fetchVbs={() => fetchVbsRegistrations(token)}
                />
              )}
              {!isSupplies && activeTab === 'survey' && (
                <AdminSurvey token={token} />
              )}
              {!isSupplies && activeTab === 'capacity' && (
                <AdminCapacity token={token} />
              )}
              {!isSupplies && activeTab === 'reports' && (
                <AdminReports
                  token={token}
                  registrations={registrations}
                  financeData={financeData}
                />
              )}
              {!isSupplies && activeTab === 'sponsorships' && (
                <AdminSponsorships token={token} />
              )}
            </>
          )}

          <div className="admin-footer">© 2026 Camp Registration</div>
        </div>
      </div>
    </div>
  );
}
